import React, { useState, useEffect } from 'react';
import { collection, addDoc, updateDoc, doc, Timestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Modal } from '@/components/ui/Modal';

interface AddEventModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    eventToEdit?: any;
}

export const AddEventModal: React.FC<AddEventModalProps> = ({ isOpen, onClose, onSuccess, eventToEdit }) => {
    const [titleEn, setTitleEn] = useState('');
    const [titleEs, setTitleEs] = useState('');
    const [eventCode, setEventCode] = useState<'IE' | 'ES' | 'PB'>('IE');
    const [eventType, setEventType] = useState<'celebration' | 'subevent'>('celebration');
    const [locationName, setLocationName] = useState('');
    const [address, setAddress] = useState('');
    const [startTime, setStartTime] = useState('');
    const [order, setOrder] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (eventToEdit) {
            setTitleEn(eventToEdit.title?.en || '');
            setTitleEs(eventToEdit.title?.es || '');
            setEventCode(eventToEdit.eventCode || eventToEdit.celebration || 'IE');
            setEventType(eventToEdit.eventType || 'celebration');
            setLocationName(eventToEdit.locationName || '');
            setAddress(eventToEdit.address || '');
            if (eventToEdit.startTime) {
                const date = eventToEdit.startTime instanceof Timestamp ? eventToEdit.startTime.toDate() : new Date(eventToEdit.startTime);
                setStartTime(date.toISOString().slice(0, 16));
            }
            setOrder(eventToEdit.order || 0);
        } else {
            setTitleEn('');
            setTitleEs('');
            setEventCode('IE');
            setEventType('celebration');
            setLocationName('');
            setAddress('');
            setStartTime('');
            setOrder(0);
        }
    }, [eventToEdit, isOpen]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const eventData = {
                title: { en: titleEn, es: titleEs },
                eventCode,
                eventType,
                locationName,
                address,
                startTime: Timestamp.fromDate(new Date(startTime)),
                order: Number(order),
                updatedAt: Timestamp.now(),
            };

            if (eventToEdit) {
                await updateDoc(doc(db, 'events', eventToEdit.id), eventData);
            } else {
                await addDoc(collection(db, 'events'), {
                    ...eventData,
                    createdAt: Timestamp.now(),
                });
            }

            onSuccess();
            onClose();
        } catch (err: any) {
            setError(err.message || 'Failed to save event');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={eventToEdit ? "Edit Event" : "Add New Event"}>
            <form onSubmit={handleSubmit} className="flex flex-col gap-4 font-sans text-secondary">
                {error && (
                    <div className="p-3 bg-red-50 text-red-700 rounded-lg border border-red-100 text-base">
                        {error}
                    </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                        <label className="block text-sm font-bold text-secondary-light uppercase tracking-widest">Title (EN)</label>
                        <input
                            type="text"
                            required
                            className="w-full p-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                            value={titleEn}
                            onChange={e => setTitleEn(e.target.value)}
                        />
                    </div>
                    <div className="space-y-1">
                        <label className="block text-sm font-bold text-secondary-light uppercase tracking-widest">Title (ES)</label>
                        <input
                            type="text"
                            required
                            className="w-full p-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                            value={titleEs}
                            onChange={e => setTitleEs(e.target.value)}
                        />
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                        <label className="block text-sm font-bold text-secondary-light uppercase tracking-widest">Event Code</label>
                        <select
                            className="w-full p-2.5 border border-gray-200 rounded-lg outline-none"
                            value={eventCode}
                            onChange={e => setEventCode(e.target.value as 'IE' | 'ES' | 'PB')}
                        >
                            <option value="IE">Ireland</option>
                            <option value="ES">Spain (Wedding)</option>
                            <option value="PB">Spain (Pre-Boda)</option>
                        </select>
                    </div>
                    <div className="space-y-1">
                        <label className="block text-sm font-bold text-secondary-light uppercase tracking-widest">Type</label>
                        <select
                            className="w-full p-2.5 border border-gray-200 rounded-lg outline-none"
                            value={eventType}
                            onChange={e => setEventType(e.target.value as 'celebration' | 'subevent')}
                        >
                            <option value="celebration">Main Celebration</option>
                            <option value="subevent">Sub-event / Itinerary</option>
                        </select>
                    </div>
                </div>

                <div className="space-y-1">
                    <label className="block text-sm font-bold text-secondary-light uppercase tracking-widest">Location Name</label>
                    <input
                        type="text"
                        required
                        className="w-full p-2.5 border border-gray-200 rounded-lg outline-none"
                        value={locationName}
                        onChange={e => setLocationName(e.target.value)}
                    />
                </div>

                <div className="space-y-1">
                    <label className="block text-sm font-bold text-secondary-light uppercase tracking-widest">Address</label>
                    <input
                        type="text"
                        className="w-full p-2.5 border border-gray-200 rounded-lg outline-none"
                        value={address}
                        onChange={e => setAddress(e.target.value)}
                    />
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                        <label className="block text-sm font-bold text-secondary-light uppercase tracking-widest">Start Time</label>
                        <input
                            type="datetime-local"
                            required
                            className="w-full p-2.5 border border-gray-200 rounded-lg outline-none"
                            value={startTime}
                            onChange={e => setStartTime(e.target.value)}
                        />
                    </div>
                    <div className="space-y-1">
                        <label className="block text-sm font-bold text-secondary-light uppercase tracking-widest">Order</label>
                        <input
                            type="number"
                            className="w-full p-2.5 border border-gray-200 rounded-lg outline-none"
                            value={order}
                            onChange={e => setOrder(Number(e.target.value))}
                        />
                    </div>
                </div>

                <div className="flex justify-end gap-3 mt-6">
                    <button type="button" className="px-6 py-2 rounded-full text-base font-bold border border-gray-200 hover:bg-gray-50 transition-colors" onClick={onClose} disabled={loading}>
                        Cancel
                    </button>
                    <button type="submit" className="px-6 py-2 rounded-full text-base font-bold bg-primary text-white hover:bg-primary-dark transition-all shadow-md active:scale-95" disabled={loading}>
                        {loading ? 'Saving...' : (eventToEdit ? 'Update Event' : 'Add Event')}
                    </button>
                </div>
            </form>
        </Modal>
    );
};
